// dailyStore.ts — Local-only daily-progress store + migration seam (Half B-a/b).
//
// Owns one localStorage key `mj.account.daily.v1`.
// Guard pattern copied from saveClient.ts `lsGet`/`lsSet` — never throws on SSR/private-mode.
//
// Migration seam (funnel-7 half):
//   `localAccountMigration` ships as a documented no-op whose `applyAccountMigration`
//   returns `{ migrated: false }` and leaves local progress intact.
//   A future foundation plan fills in the server endpoint + idempotent merge.
//
// SEAM: when server-side account persistence lands, implement:
//   1. A POST /api/daily/migrate endpoint that accepts DailyProgress + userId and
//      performs an idempotent merge (max lifetimeProgressPt, union claimedDays,
//      additive en once) keyed by userId.
//   2. Replace the no-op body of `localAccountMigration.applyAccountMigration` with
//      a fetch to that endpoint, then clear the local store on success.
//   See: docs/superpowers/plans/funnel-daily-progress.md §Seam for the full contract.
import type { DailyProgress } from '@mj/engine';
import { dailyPlayReward, applyDailyReward } from '@mj/engine';

// ---------------------------------------------------------------------------
// localStorage helpers (guarded: no-op when unavailable, e.g. SSR/private-mode)
// ---------------------------------------------------------------------------

const LS_KEY = 'mj.account.daily.v1';

function lsSet(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function lsGet(key: string): unknown {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Validation / default
// ---------------------------------------------------------------------------

function zeroDailyProgress(): DailyProgress {
  return {
    dayKey: '',
    matchesToday: 0,
    lifetimeProgressPt: 0,
    en: 0,
    claimedDays: 0,
  };
}

function isDailyProgress(v: unknown): v is DailyProgress {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.dayKey === 'string' &&
    typeof o.matchesToday === 'number' &&
    typeof o.lifetimeProgressPt === 'number' &&
    typeof o.en === 'number' &&
    typeof o.claimedDays === 'number'
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Load the local daily-progress value. Returns a fresh zero value if absent or malformed. */
export function loadDaily(): DailyProgress {
  const raw = lsGet(LS_KEY);
  if (isDailyProgress(raw)) return raw;
  return zeroDailyProgress();
}

/** Persist the daily-progress value. Silent no-op when localStorage is unavailable. */
export function saveDaily(p: DailyProgress): void {
  lsSet(LS_KEY, p);
}

/** Record one match played, update localStorage, and return the reward + next progress. */
export function recordMatchPlayed(nowMs = Date.now()): {
  reward: ReturnType<typeof dailyPlayReward>;
  progress: DailyProgress;
} {
  const current = loadDaily();
  const reward = dailyPlayReward(current, nowMs);
  const next = applyDailyReward(current, nowMs);
  saveDaily(next);
  return { reward, progress: next };
}

// ---------------------------------------------------------------------------
// Migration seam (funnel-7)
// ---------------------------------------------------------------------------

/** The local, not-yet-migrated progress. Null if nothing has ever been stored. */
export interface AccountMigration {
  pendingDaily(): DailyProgress | null;
  applyAccountMigration(userId: string): Promise<{ migrated: boolean }>;
}

export const localAccountMigration: AccountMigration = {
  pendingDaily(): DailyProgress | null {
    const raw = lsGet(LS_KEY);
    if (!isDailyProgress(raw)) return null;
    // Only pending if the user has actually earned something
    if (raw.lifetimeProgressPt === 0 && raw.claimedDays === 0) return null;
    return raw;
  },

  // SEAM: no-op until server-side foundation lands.
  // When the foundation plan is implemented, this should:
  //   1. POST the local DailyProgress to /api/daily/migrate with userId.
  //   2. The server performs idempotent merge (max lifetimeProgressPt, union claimedDays, additive en once).
  //   3. On success, clear the local store (or mark it as migrated) and return { migrated: true }.
  // Until then: local progress is preserved (non-destructive), login is inert.
  async applyAccountMigration(_userId: string): Promise<{ migrated: boolean }> {
    return { migrated: false };
  },
};
